import { useEffect, useEffectEvent, useState } from "react";

import {
  shouldAskForSuggestion,
  type SuggestionAnswer,
  type SuggestionQuestion,
  type SuggestionOutcome,
  type SuggestWord,
} from "./suggestion";

// Unmeasured: long enough to skip a quick double tap, short enough that Done,
// which waits on the answer, still follows a settled word promptly. A slower
// child's every letter is asked; the abort ends each superseded request.
const SUGGEST_DEBOUNCE_MS = 300;

const FAILED: SuggestionOutcome = { kind: "failed" };

export interface Suggestion {
  readonly answer: SuggestionAnswer | null;
  /** Drops the answer once its word is committed, so the next word never sees it. */
  readonly forget: () => void;
}

export const useSuggestion = (
  suggest: SuggestWord | undefined,
  { prefix: word, context }: SuggestionQuestion,
): Suggestion => {
  const [answer, setAnswer] = useState<SuggestionAnswer | null>(null);
  const enabled = suggest !== undefined;

  const ask = useEffectEvent(
    (prefix: string, signal: AbortSignal): Promise<SuggestionOutcome> =>
      suggest === undefined
        ? Promise.resolve(FAILED)
        : suggest(prefix, { signal }).then(
            (response): SuggestionOutcome => ({ kind: "answered", response }),
            () => FAILED,
          ),
  );

  useEffect(() => {
    if (!enabled || !shouldAskForSuggestion(word)) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void ask(word, controller.signal).then((outcome) => {
        if (!controller.signal.aborted)
          setAnswer({ prefix: word, context, outcome });
      });
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [enabled, word, context]);

  return { answer: enabled ? answer : null, forget: () => setAnswer(null) };
};
